import { useEffect, useState, useRef } from "react";
import { Shield, Clock, Users, ChevronDown } from "lucide-react";

interface AppleHeroProps {
  overline?: string;
  title: string[];
  cta: string;
  ctaLink?: string;
  secondaryCta?: string;
  secondaryCtaLink?: string;
  backgroundVideo?: string;
  trustText?: {
    line1: string;
    line2: string;
  };
  showSocialProof?: boolean;
  proofResult?: {
    text: string;
    highlight: string;
  };
}

const AppleHero = ({ 
  overline, title, cta, ctaLink = "#demo", 
  secondaryCta, secondaryCtaLink,
  backgroundVideo, trustText, showSocialProof = false,
  proofResult
}: AppleHeroProps) => {
  const [scrollY, setScrollY] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [proofVisible, setProofVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    if (backgroundVideo) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [backgroundVideo]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.play().catch(() => {});
  }, []);

  // Animate proof result after a delay
  useEffect(() => {
    if (proofResult) {
      const timer = setTimeout(() => setProofVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [proofResult]);

  return (
    <section className={`relative min-h-screen flex items-center justify-center overflow-hidden ${backgroundVideo ? '-mt-[180px] pt-[180px]' : 'bg-background'} py-64`}>
      {/* Video de fondo con parallax */}
      {backgroundVideo && (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 z-0" />
          <video 
            ref={videoRef}
            autoPlay 
            loop 
            muted 
            playsInline
            preload="auto"
            
            className="absolute inset-0 w-full h-full object-cover z-0 transition-transform duration-100 ease-out"
            style={{
              transform: `translateY(${scrollY * 0.5}px) scale(1.1)`
            }}
          >
            <source src={backgroundVideo} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/40 z-[1]" />
        </>
      )}
      
      {/* Contenido */}
      <div className="relative z-10 max-w-7xl mx-auto px-8 text-center">
        {overline && (
          <p className={`text-xl md:text-2xl font-light mb-8 animate-fade-in ${
            backgroundVideo 
              ? 'text-white/90 [text-shadow:0_2px_12px_rgba(0,0,0,0.5)]' 
              : 'text-muted-foreground'
          }`}>
            {overline}
          </p>
        )}
        <h1 className={`text-5xl md:text-6xl lg:text-7xl font-bold leading-[0.9] tracking-tight mb-16 animate-fade-in ${
          backgroundVideo 
            ? 'text-white [text-shadow:0_4px_20px_rgba(0,0,0,0.6)]' 
            : 'text-foreground'
        }`}>
          {title.map((line, index) => (
            <div key={index} className="mb-2">
              {line}
            </div>
          ))}
        </h1>

        {/* Dual CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
          <a
            href={ctaLink}
            className={`inline-block px-12 py-5 text-xl rounded-full transition-all duration-300 font-medium ${
              backgroundVideo 
                ? 'bg-white text-black hover:bg-gray-100 shadow-2xl hover:shadow-[0_20px_60px_rgba(255,255,255,0.3)] hover:scale-105' 
                : 'bg-primary text-primary-foreground hover:bg-primary/90'
            }`}
          >
            {cta}
          </a>
          {secondaryCta && (
            <a
              href={secondaryCtaLink || "#contact"}
              className={`inline-block px-12 py-5 text-xl rounded-full transition-all duration-300 font-medium border-2 ${
                backgroundVideo 
                  ? 'border-white/60 text-white hover:bg-white/10 [text-shadow:0_2px_8px_rgba(0,0,0,0.4)]' 
                  : 'border-border text-foreground hover:bg-muted'
              }`}
            >
              {secondaryCta}
            </a>
          )}
        </div>
        
        {/* Trust Text */}
        {trustText && (
          <div className={`mt-6 space-y-1 animate-fade-in ${
            backgroundVideo 
              ? 'text-white/70 [text-shadow:0_2px_8px_rgba(0,0,0,0.4)]' 
              : 'text-muted-foreground'
          }`}>
            <p className="text-base md:text-lg font-light">{trustText.line1}</p>
            <p className="text-base md:text-lg font-light">{trustText.line2}</p>
          </div>
        )}

        {/* Animated Proof Result */}
        {proofResult && (
          <div 
            className={`mt-8 transition-all duration-700 ease-out ${
              proofVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className={`inline-flex items-center gap-3 px-6 py-3 rounded-full backdrop-blur-md ${
              backgroundVideo 
                ? 'bg-white/10 border border-white/20' 
                : 'bg-muted border border-border'
            }`}>
              <span className={`text-sm md:text-base font-light ${
                backgroundVideo ? 'text-white/80' : 'text-muted-foreground'
              }`}>
                {proofResult.text}
              </span>
              <span className={`text-sm md:text-base font-semibold ${
                backgroundVideo ? 'text-white' : 'text-foreground'
              }`}>
                {proofResult.highlight}
              </span>
            </div>
          </div>
        )}

        {/* Social Proof Badges */}
        {showSocialProof && (
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4 sm:gap-6 animate-fade-in">
            <div className={`flex items-center gap-2 px-5 py-2.5 rounded-full backdrop-blur-sm ${
              backgroundVideo 
                ? 'bg-white/10 border border-white/20 text-white/90' 
                : 'bg-muted border border-border text-foreground'
            }`}>
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">23 años de experiencia</span>
            </div>
            <div className={`flex items-center gap-2 px-5 py-2.5 rounded-full backdrop-blur-sm ${
              backgroundVideo 
                ? 'bg-white/10 border border-white/20 text-white/90' 
                : 'bg-muted border border-border text-foreground'
            }`}>
              <Users className="w-4 h-4" />
              <span className="text-sm font-medium">150+ clientes activos</span>
            </div>
            <div className={`flex items-center gap-2 px-5 py-2.5 rounded-full backdrop-blur-sm ${
              backgroundVideo 
                ? 'bg-white/10 border border-white/20 text-white/90' 
                : 'bg-muted border border-border text-foreground'
            }`}>
              <Shield className="w-4 h-4" />
              <span className="text-sm font-medium">Sin compromiso</span>
            </div>
          </div>
        )}
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <ChevronDown className={`w-8 h-8 ${
          backgroundVideo ? 'text-white/50' : 'text-muted-foreground/50'
        }`} />
      </div>
    </section>
  );
};

export default AppleHero;
